// App.js
import React, { useEffect } from "react";
import "./App.css";
import "./global.css";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import Sidebar from "./components/sidebar";
import Menu from "./components/Menu/Menu";
import Home from "./components/Home";
import Help from "./components/Help";
import Tables from "./components/Tables/Tables";
import Data from "./components/DataCenter/Data";
import Users from "./components/Users/Users";
import Settings from "./components/Settings/Settings";
import License from "./components/Settings/License/License";
import ManageQuestions from "./components/Settings/ManageQuestions";
import PaymentGatewayLog from "./components/Settings/PaymentGatewayLog";
import DayEndAudit from "./components/DataCenter/DayEndAudit";

import SignOut from "./components/SignOut";
import Footer from "./components/Footer";
import RequireAuth from "./auth/RequireAuth";
import { 
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Contact from "./components/Contact";
import SignOutConfirm from "./components/SignOutConfirm";
// NEW imports
import { AuthProvider, useAuth } from "./auth/AuthContext";
import LoginPage from "./components/LoginPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import ViewAsPage from "./components/ViewAsPage";
import BrowseDiningSessions from "./components/DataCenter/BrowseDiningSessions";

function App() {
  return (
    <AuthProvider>
      <Router>
        <SidebarProvider>
          <AppContent />
        </SidebarProvider>
      </Router>
    </AuthProvider>
  );
}



function AppContent() {
  const { isAuthenticated, user, primaryRestaurantId, tenantServerUrl, viewAsEmail, logout } =
    useAuth();
  const location = useLocation();
  const { isSidebarOpen, setSidebarOpen, toggleSidebar } = useSidebar();

  // Phone and tablet drawers should close once a page is chosen.
  useEffect(() => {
    if (window.matchMedia("(max-width: 1023px)").matches) {
      setSidebarOpen(false);
    }
  }, [location.pathname, setSidebarOpen]);

  // If we navigated to /signout with state.backgroundLocation,
  // keep rendering the "background" page and overlay the modal.
  const state =
    location.state && location.state.backgroundLocation ? location.state : null;
  const backgroundLocation = state && state.backgroundLocation;

  // logged-in username (if you want to display it)
  const loggedInUser_Username = user?.email || "admin";

  return (
    <>
      {/* Only show sidebar when logged in */}
      {isAuthenticated && (
        <>
          <Sidebar isOpen={isSidebarOpen} />
          {isSidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
              onKeyDown={(e) => e.key === "Escape" && setSidebarOpen(false)}
              role="button"
              tabIndex={-1}
              aria-hidden="true"
            />
          )}
        </>
      )}

      {viewAsEmail && (
        <div className="tm-view-as-bar">
          <span>Viewing MyData as {viewAsEmail}</span>
          <button type="button" onClick={logout}>Exit</button>
        </div>
      )}
      <div className={`App${["/login", "/forgot-password", "/reset-password", "/view-as"].includes(location.pathname) ? " App--login" : ""}`}>
        {/* Main routes – use backgroundLocation if present */}
        <Routes location={backgroundLocation || location}>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/view-as" element={<ViewAsPage />} />

          {/* Protected routes (require user + token + restaurant id) */}
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Home />} />
            <Route path="/menu" element={<Menu />} />

            <Route
              path="/tables"
              element={
                <Tables
                  restaurantId={primaryRestaurantId}
                  loggedInUser_Username={loggedInUser_Username}
                  serverUrl={tenantServerUrl}
                />
              }
            />

            <Route
              path="/data"
              element={
                <Data
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                />
              }
            />

            <Route path="/users" element={<Users />} />
            <Route path="/settings" element={<Settings />} />
             <Route path="/settings/questions" element={<ManageQuestions />} />
             <Route path="/settings/payment-gateway-log" element={<PaymentGatewayLog />} />
             <Route path="/data/day-end-audit" element={<DayEndAudit />} />
             <Route
              path="/settings/questions/6/:questionCategoryID/:questionID"
              element={
                <ManageQuestions
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                />
              }
            />
            <Route path="/license" element={<License />} />

            {/* When visited directly (no background), this still works as a normal page */}
            <Route path="/signout" element={<SignOut />} />
            <Route path="/signout-confirm" element={<SignOutConfirm />} />

            <Route
              path="/data/:categoryID/:chartID"
              element={
                <Data
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                />
              }
            />

            <Route
              path="/data/6/:questionCategoryID/:questionID"
              element={
                <Data
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                />
              }
            />
             <Route
              path="/data/browse/dining/:sessionID"
              element={
                <Data
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                  browseDiningSessions={true}
                />
              }
            />
            <Route
              path="/data/browse/dining/"
              element={
                <Data
                  RestaurantID={primaryRestaurantId}
                  serverUrl={tenantServerUrl}
                  browseDiningSessions={true}
                />
              }
            />

            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
        </Routes>

        {/* Modal routes – only when we have a backgroundLocation */}
        {backgroundLocation && (
          <Routes>
            <Route element={<RequireAuth />}>
              <Route path="/signout" element={<SignOut />} />
            </Route>
          </Routes>
        )}

        {/* Only show footer when logged in (optional) */}
        {isAuthenticated && <Footer />}
      </div>
    </>
  );
}

export default App;
